import React, { useState, useCallback } from "react";
import { db } from "@/api/db";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PROFESSION_NAMES } from "@/data/professions";
import {
  ArrowRight,
  ArrowLeft,
  Shield,
  Award,
  TrendingUp,
  Send,
  ChevronDown,
  CheckCircle2,
  User,
  Phone,
  Package,
  Users,
  DollarSign,
  Link2,
  Briefcase,
  Sparkles,
  ExternalLink,
} from "lucide-react";

const STEPS = [
  {
    id: "about-you",
    title: "Quem é você?",
    subtitle: "Conte-nos sobre você ou sua escola. Essas informações nos ajudam a entender quem está por trás do produto.",
    icon: User,
  },
  {
    id: "your-product",
    title: "Sobre seu produto.",
    subtitle: "Descreva seu curso ou formação. Queremos entender o que torna seu produto especial e para quem ele foi feito.",
    icon: Package,
  },
  {
    id: "commercial",
    title: "Condições comerciais.",
    subtitle: "Defina preço, descontos e comissão. Trabalhamos com transparência para uma parceria de longo prazo.",
    icon: DollarSign,
  },
  {
    id: "professions",
    title: "Para quem é indicado?",
    subtitle: "Selecione as profissões que mais se beneficiam do seu produto. Isso nos ajuda a recomendar para as pessoas certas.",
    icon: Briefcase,
  },
  {
    id: "review",
    title: "Tudo pronto.",
    subtitle: "Revise suas informações e envie sua aplicação.",
    icon: Sparkles,
  },
];

const LEVEL_COLORS = {
  Iniciante: { bg: "#D5F9D7", text: "#008572" },
  "Avançado": { bg: "#FADBDF", text: "#892E38" },
};

// ─── Animation Variants ───────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

const stepVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: (direction) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0,
    transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

// ─── Mock Course Card ─────────────────────────────────────────────────────────

const HERO_PROFILES = [
  { name: "Ana Oliveira", role: "Social Media", photo: "https://i.pravatar.cc/80?img=1" },
  { name: "Carlos Mendes", role: "Gestor de Tráfego", photo: "https://i.pravatar.cc/80?img=3" },
  { name: "Julia Santos", role: "Copywriter", photo: "https://i.pravatar.cc/80?img=5" },
  { name: "Pedro Almeida", role: "Designer", photo: "https://i.pravatar.cc/80?img=7" },
  { name: "Marina Costa", role: "SDR", photo: "https://i.pravatar.cc/80?img=9" },
  { name: "Rafael Lima", role: "Closer", photo: "https://i.pravatar.cc/80?img=11" },
  { name: "Fernanda Souza", role: "Estrategista Digital", photo: "https://i.pravatar.cc/80?img=16" },
  { name: "Lucas Ribeiro", role: "Editor de Vídeos", photo: "https://i.pravatar.cc/80?img=12" },
  { name: "Camila Reis", role: "Analista de CRM", photo: "https://i.pravatar.cc/80?img=20" },
  { name: "Thiago Borges", role: "Videomaker", photo: "https://i.pravatar.cc/80?img=14" },
  { name: "Beatriz Lopes", role: "Redator", photo: "https://i.pravatar.cc/80?img=23" },
  { name: "Diego Ferreira", role: "Web Designer", photo: "https://i.pravatar.cc/80?img=33" },
  { name: "Isabela Nunes", role: "Customer Success", photo: "https://i.pravatar.cc/80?img=25" },
  { name: "Gustavo Prado", role: "Gestor Comercial", photo: "https://i.pravatar.cc/80?img=53" },
  { name: "Larissa Duarte", role: "Social Seller", photo: "https://i.pravatar.cc/80?img=29" },
  { name: "Mateus Vieira", role: "Desenvolvedor", photo: "https://i.pravatar.cc/80?img=51" },
  { name: "Priscila Moura", role: "Gestor de Comunidade", photo: "https://i.pravatar.cc/80?img=32" },
  { name: "Roberto Dias", role: "Sales Ops", photo: "https://i.pravatar.cc/80?img=52" },
  { name: "Tatiana Gomes", role: "Analista de Marketing", photo: "https://i.pravatar.cc/80?img=44" },
  { name: "Vinícius Castro", role: "BDR", photo: "https://i.pravatar.cc/80?img=57" },
  { name: "Amanda Rocha", role: "E-mail Marketing", photo: "https://i.pravatar.cc/80?img=47" },
  { name: "Felipe Martins", role: "Product Manager", photo: "https://i.pravatar.cc/80?img=59" },
  { name: "Natália Campos", role: "Gerente de Projetos", photo: "https://i.pravatar.cc/80?img=45" },
  { name: "Henrique Azevedo", role: "Estrategista de Conteúdo", photo: "https://i.pravatar.cc/80?img=60" },
];

const HERO_PROFILE_ROWS = [
  { profiles: HERO_PROFILES.slice(0, 6), direction: "left", speed: 80, offset: 1560 },
  { profiles: HERO_PROFILES.slice(6, 12), direction: "right", speed: 90, offset: 1560 },
  { profiles: HERO_PROFILES.slice(12, 18), direction: "left", speed: 85, offset: 1560 },
  { profiles: HERO_PROFILES.slice(18, 24), direction: "right", speed: 95, offset: 1560 },
  { profiles: HERO_PROFILES.slice(0, 6), direction: "left", speed: 75, offset: 1560 },
  { profiles: HERO_PROFILES.slice(6, 12), direction: "right", speed: 88, offset: 1560 },
  { profiles: HERO_PROFILES.slice(12, 18), direction: "left", speed: 92, offset: 1560 },
  { profiles: HERO_PROFILES.slice(18, 24), direction: "right", speed: 82, offset: 1560 },
];

const SHOWCASE_COURSES = [
  { title: "Social Media: Estratégias para Engajamento", instructor: "Ana Oliveira", level: "Iniciante", price: "97", originalPrice: "197", discount: 51, gradient: "linear-gradient(135deg, #EAFAF6 0%, #B0FCEA 100%)" },
  { title: "Gestão de Tráfego Pago: Meta Ads e Google Ads", instructor: "Carlos Mendes", level: "Avançado", price: "247", gradient: "linear-gradient(135deg, #FFF0F0 0%, #FADBDF 100%)" },
  { title: "Copywriting para Alta Conversão", instructor: "Julia Santos", level: "Iniciante", price: "147", originalPrice: "297", discount: 50, gradient: "linear-gradient(135deg, #F0F4FF 0%, #D6E0FF 100%)" },
  { title: "SDR: Prospecção e Qualificação de Leads", instructor: "Marina Costa", level: "Avançado", price: "197", gradient: "linear-gradient(135deg, #FFF8E1 0%, #FEEDB4 100%)" },
  { title: "Design para Redes Sociais com Figma", instructor: "Pedro Almeida", level: "Iniciante", price: "127", originalPrice: "247", discount: 49, gradient: "linear-gradient(135deg, #EAFAF6 0%, #B0FCEA 100%)" },
  { title: "Customer Success: Retenção e Expansão", instructor: "Camila Reis", level: "Avançado", price: "297", gradient: "linear-gradient(135deg, #F5F0FF 0%, #E0D4FF 100%)" },
  { title: "E-mail Marketing: Automações que Vendem", instructor: "Rafael Lima", level: "Iniciante", price: "167", originalPrice: "327", discount: 49, gradient: "linear-gradient(135deg, #FFF0F0 0%, #FADBDF 100%)" },
  { title: "Edição de Vídeo para Reels e TikTok", instructor: "Fernanda Souza", level: "Avançado", price: "347", gradient: "linear-gradient(135deg, #F0F4FF 0%, #D6E0FF 100%)" },
];

function ShowcaseCard({ title, instructor, level, price, originalPrice, discount, gradient }) {
  const lc = LEVEL_COLORS[level] || { bg: "#F2F4F4", text: "#646C6F" };
  return (
    <div
      className="rounded-2xl flex flex-col overflow-hidden flex-shrink-0"
      style={{ background: "#fff", width: 260 }}
    >
      <div className="relative" style={{ aspectRatio: "16/9", background: gradient }}>
        <div className="w-full h-full flex items-center justify-center">
          <span className="text-3xl opacity-20">📚</span>
        </div>
        {discount && (
          <span className="absolute top-2 left-2 px-2.5 py-1 rounded-full text-xs font-semibold shadow-md"
            style={{ background: "#00DCA4", color: "#003326" }}>
            {discount}% off
          </span>
        )}
      </div>
      <div className="p-3.5 flex flex-col flex-1 gap-2">
        <div className="flex items-start gap-1.5">
          <p className="font-semibold text-xs leading-snug line-clamp-2" style={{ color: "#2E3233" }}>{title}</p>
          <span className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#00DCA4" }}>
            <svg width="7" height="6" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.2 5.8L8 1" stroke="#003326" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "#F2F4F4" }}>
            <User className="w-2.5 h-2.5" style={{ color: "#99A6AB" }} />
          </div>
          <span className="text-xs" style={{ color: "#646C6F" }}>{instructor}</span>
        </div>
        <span className="self-start px-1.5 py-0.5 rounded-full text-xs font-medium" style={{ background: lc.bg, color: lc.text }}>
          {level}
        </span>
        <div className="flex items-center gap-1.5 mt-auto">
          {originalPrice && <span className="text-xs line-through" style={{ color: "#99A6AB" }}>R$ {originalPrice}</span>}
          <span className="text-xs font-bold" style={{ color: "#003326" }}>R$ {price}</span>
        </div>
        <div className="flex items-center justify-center gap-1.5 w-full py-2 rounded-full text-xs font-bold"
          style={{ background: "#00DCA4", color: "#003326" }}>
          Ver curso <ExternalLink className="w-3 h-3" />
        </div>
      </div>
    </div>
  );
}

// ─── Showcase Carousel ────────────────────────────────────────────────────────

function ShowcaseCarousel({ courses }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const doubled = [...courses, ...courses];
  const cardWidth = 280;
  const gap = 24;
  const totalWidth = courses.length * (cardWidth + gap);
  const total = doubled.length;

  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, 2000);
    return () => clearInterval(interval);
  }, [total]);

  return (
    <div className="relative" style={{ overflow: "clip" }}>
      <div
        className="px-8"
        style={{
          maskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
          WebkitMaskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
        }}
      >
        <motion.div
          className="flex items-center py-10"
          style={{ gap }}
          animate={{ x: [0, -totalWidth] }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear", repeatType: "loop" }}
        >
          {doubled.map((course, i) => {
            const isActive = i === activeIndex;
            return (
              <motion.div
                key={`${course.title}-${i}`}
                className="flex-shrink-0"
                style={{ width: cardWidth }}
                animate={{
                  scale: isActive ? 1.08 : 0.96,
                  y: isActive ? -8 : 0,
                }}
                transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                <motion.div
                  style={{ borderRadius: 16, overflow: "hidden" }}
                  animate={{
                    boxShadow: isActive
                      ? "0 25px 60px rgba(0,51,38,0.25), 0 0 40px rgba(0,220,164,0.2)"
                      : "0 8px 24px rgba(0,0,0,0.06)",
                  }}
                  transition={{ duration: 0.6 }}
                >
                  <ShowcaseCard {...course} />
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
}

// ─── Step Progress Bar ────────────────────────────────────────────────────────

function StepProgressBar({ currentStep, totalSteps }) {
  const progress = ((currentStep + 1) / totalSteps) * 100;
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium" style={{ color: "#99A6AB" }}>
          Passo {currentStep + 1} de {totalSteps}
        </span>
        <span className="text-xs font-medium" style={{ color: "#00DCA4" }}>
          {Math.round(progress)}%
        </span>
      </div>
      <div className="h-1 w-full rounded-full overflow-hidden" style={{ background: "rgba(0,220,164,0.12)" }}>
        <motion.div
          className="h-full rounded-full"
          style={{ background: "linear-gradient(90deg, #00DCA4, #00F0B5)" }}
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        />
      </div>
    </div>
  );
}

// ─── Pill Toggle ──────────────────────────────────────────────────────────────

function PillToggle({ options, value, onChange }) {
  return (
    <div className="flex gap-2">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onChange(opt.value)}
            className="flex-1 py-3 rounded-2xl text-sm font-semibold transition-all duration-300"
            style={
              active
                ? { background: "#003326", color: "#fff", boxShadow: "0 4px 20px rgba(0,51,38,0.2)" }
                : { background: "#F2F4F4", color: "#646C6F" }
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Styled Form Field ────────────────────────────────────────────────────────

function FormField({ label, required, error, hint, children }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium block" style={{ color: error ? "#892E38" : "#2E3233" }}>
        {label}
        {required && <span className="ml-0.5" style={{ color: "#00DCA4" }}>*</span>}
        {hint && <span className="font-normal ml-1.5 text-xs" style={{ color: "#99A6AB" }}>{hint}</span>}
      </label>
      {children}
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs"
          style={{ color: "#892E38" }}
        >
          Campo obrigatório
        </motion.p>
      )}
    </div>
  );
}

// ─── Review Item ──────────────────────────────────────────────────────────────

function ReviewItem({ label, value }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="flex flex-col gap-0.5 py-3" style={{ borderBottom: "1px solid #F2F4F4" }}>
      <span className="text-xs font-medium" style={{ color: "#99A6AB" }}>{label}</span>
      <span className="text-sm font-medium" style={{ color: "#2E3233" }}>
        {Array.isArray(value) ? value.join(", ") : value}
      </span>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PartnerApplication() {
  const [view, setView] = useState("landing"); // "landing" | "form" | "success"
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    expert_name: "",
    phone: "",
    product_name: "",
    target_audience: "",
    level: "Iniciante",
    product_price: "",
    has_discount: false,
    discount_value: "",
    commission_pct: "",
    sales_page_url: "",
    platform: "",
    professions: [],
    other_professions: [],
    is_perpetual: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function toggleProfession(prof) {
    setForm((f) => {
      const list = f.professions || [];
      return { ...f, professions: list.includes(prof) ? list.filter((p) => p !== prof) : [...list, prof] };
    });
  }

  const enterForm = useCallback(() => {
    setView("form");
    setCurrentStep(0);
    setDirection(1);
  }, []);

  // ─── Step Validation ──────────────────────────────────────────────────────

  function validateStep(step) {
    const errs = {};
    if (step === 0) {
      if (!form.expert_name.trim()) errs.expert_name = true;
      if (!form.phone.trim()) errs.phone = true;
    } else if (step === 1) {
      if (!form.product_name.trim()) errs.product_name = true;
      if (!form.target_audience.trim()) errs.target_audience = true;
    } else if (step === 2) {
      if (!form.product_price.trim()) errs.product_price = true;
      if (!form.sales_page_url.trim()) errs.sales_page_url = true;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function nextStep() {
    if (!validateStep(currentStep)) return;
    setDirection(1);
    setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
    setErrors({});
  }

  function prevStep() {
    setDirection(-1);
    setCurrentStep((s) => Math.max(s - 1, 0));
    setErrors({});
  }

  function goToStep(step) {
    setDirection(step > currentStep ? 1 : -1);
    setCurrentStep(step);
    setErrors({});
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const payload = {
        expert_name: form.expert_name.trim(),
        phone: form.phone.trim(),
        product_name: form.product_name.trim(),
        target_audience: form.target_audience.trim(),
        level: form.level,
        product_price: form.product_price.trim(),
        has_discount: form.has_discount,
        discount_value: form.has_discount ? form.discount_value.trim() : null,
        commission_pct: form.commission_pct.trim() || null,
        sales_page_url: form.sales_page_url.trim(),
        platform: form.platform.trim() || null,
        professions: [...form.professions, ...form.other_professions],
        is_perpetual: form.is_perpetual,
      };
      await db.entities.PartnerApplication.create(payload);
      setView("success");
    } catch (err) {
      console.error("Submit error:", err);
      alert("Erro ao enviar: " + (err?.message || JSON.stringify(err)));
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Step Content Renderer ────────────────────────────────────────────────

  function renderStepContent(step) {
    const inputStyle = "h-12 rounded-2xl border-2 border-transparent bg-[#F7F8F8] px-4 text-base transition-all duration-200 focus:border-[#00DCA4] focus:bg-white focus:ring-0 focus:outline-none placeholder:text-[#C0C7CA]";
    const textareaStyle = "rounded-2xl border-2 border-transparent bg-[#F7F8F8] px-4 py-3 text-base transition-all duration-200 focus:border-[#00DCA4] focus:bg-white focus:ring-0 focus:outline-none placeholder:text-[#C0C7CA] resize-none";

    switch (step) {
      case 0:
        return (
          <div className="space-y-6">
            <FormField label="Nome do Expert ou Escola" required error={errors.expert_name}>
              <Input
                value={form.expert_name}
                onChange={(e) => set("expert_name", e.target.value)}
                placeholder="Ex: João Silva Academy"
                className={inputStyle}
              />
            </FormField>
            <FormField label="Telefone" required error={errors.phone}>
              <Input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="(11) 99999-9999"
                className={inputStyle}
              />
            </FormField>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <FormField label="Nome do produto" required error={errors.product_name}>
              <Input
                value={form.product_name}
                onChange={(e) => set("product_name", e.target.value)}
                placeholder="Ex: Marketing Digital do Zero ao Avançado"
                className={inputStyle}
              />
            </FormField>
            <FormField label="Público-alvo" required error={errors.target_audience}>
              <Textarea
                value={form.target_audience}
                onChange={(e) => set("target_audience", e.target.value)}
                placeholder="Descreva o perfil ideal de aluno para este produto..."
                className={textareaStyle}
                rows={3}
              />
            </FormField>
            <FormField label="Você recomenda para qual tipo de público?">
              <PillToggle
                options={[
                  { label: "Iniciante", value: "Iniciante" },
                  { label: "Avançado", value: "Avançado" },
                  { label: "Ambos", value: "Ambos" },
                ]}
                value={form.level}
                onChange={(v) => set("level", v)}
              />
            </FormField>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <FormField label="Preço do produto" required error={errors.product_price}>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold select-none pointer-events-none" style={{ color: "#99A6AB" }}>R$</span>
                <Input
                  value={form.product_price}
                  onChange={(e) => set("product_price", e.target.value)}
                  placeholder="197"
                  className={`${inputStyle} pl-10`}
                />
              </div>
            </FormField>
            <FormField label="Oferece desconto para usuários da Portüs?" hint="(recomendamos para maior conversão)">
              <div className="space-y-3">
                <PillToggle
                  options={[
                    { label: "Sim", value: true },
                    { label: "Não", value: false },
                  ]}
                  value={form.has_discount}
                  onChange={(v) => set("has_discount", v)}
                />
                <AnimatePresence>
                  {form.has_discount && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold select-none pointer-events-none" style={{ color: "#99A6AB" }}>R$</span>
                        <Input
                          value={form.discount_value}
                          onChange={(e) => set("discount_value", e.target.value)}
                          placeholder="Valor com desconto"
                          className={`${inputStyle} pl-10`}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </FormField>
            <FormField label="Comissão" hint="(percentual liberado para a Portüs por venda realizada)">
              <div className="relative">
                <Input
                  value={form.commission_pct}
                  onChange={(e) => set("commission_pct", e.target.value.replace(/[^0-9]/g, ""))}
                  placeholder="Ex: 40%"
                  inputMode="numeric"
                  className={`${inputStyle} pr-10`}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold select-none pointer-events-none" style={{ color: "#99A6AB" }}>%</span>
              </div>
            </FormField>
            <FormField label="Página de vendas" hint="(queremos conhecer melhor o produto)" required error={errors.sales_page_url}>
              <Input
                value={form.sales_page_url}
                onChange={(e) => set("sales_page_url", e.target.value)}
                placeholder="https://..."
                type="url"
                className={inputStyle}
              />
            </FormField>
            <FormField label="Através de qual plataforma é vendido?">
              <Input
                value={form.platform}
                onChange={(e) => set("platform", e.target.value)}
                placeholder="Ex: Hotmart, Kiwify, Eduzz, Monetizze..."
                className={inputStyle}
              />
            </FormField>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <FormField label="Profissões indicadas" hint="(selecione todas que se aplicam)">
              <div className="flex gap-2 flex-wrap">
                {PROFESSION_NAMES.map((prof) => {
                  const active = form.professions.includes(prof);
                  return (
                    <motion.button
                      key={prof}
                      type="button"
                      onClick={() => toggleProfession(prof)}
                      whileTap={{ scale: 0.95 }}
                      className="px-3.5 py-2 rounded-xl text-xs font-medium transition-all duration-200"
                      style={
                        active
                          ? { background: "#003326", color: "#fff", boxShadow: "0 2px 12px rgba(0,51,38,0.15)" }
                          : { background: "#F2F4F4", color: "#646C6F" }
                      }
                    >
                      {prof}
                    </motion.button>
                  );
                })}
              </div>
            </FormField>
            <FormField label="Outras profissões" hint="(digite e aperte Enter)">
              <div className="space-y-2">
                {form.other_professions.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {form.other_professions.map((prof) => (
                      <motion.span
                        key={prof}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium"
                        style={{ background: "#003326", color: "#fff" }}
                      >
                        {prof}
                        <button
                          type="button"
                          onClick={() => set("other_professions", form.other_professions.filter((p) => p !== prof))}
                          className="hover:opacity-70"
                          style={{ color: "#00DCA4" }}
                        >
                          &times;
                        </button>
                      </motion.span>
                    ))}
                  </div>
                )}
                <Input
                  placeholder="Ex: Product Designer, Scrum Master..."
                  className="h-12 rounded-2xl border-2 border-transparent bg-[#F7F8F8] px-4 text-sm transition-all duration-200 focus:border-[#00DCA4] focus:bg-white focus:ring-0 focus:outline-none placeholder:text-[#C0C7CA]"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const val = e.target.value.trim();
                      if (val && !form.other_professions.includes(val)) {
                        set("other_professions", [...form.other_professions, val]);
                        e.target.value = "";
                      }
                    }
                  }}
                />
              </div>
            </FormField>
            <FormField label="Produto perpétuo?">
              <PillToggle
                options={[
                  { label: "Sim, sempre disponível", value: true },
                  { label: "Não, tem prazo", value: false },
                ]}
                value={form.is_perpetual}
                onChange={(v) => set("is_perpetual", v)}
              />
            </FormField>
          </div>
        );

      case 4:
        return (
          <div className="space-y-1">
            <div className="rounded-2xl p-6" style={{ background: "#F7F8F8" }}>
              <ReviewItem label="Nome" value={form.expert_name} />
              <ReviewItem label="Telefone" value={form.phone} />
              <ReviewItem label="Produto" value={form.product_name} />
              <ReviewItem label="Público-alvo" value={form.target_audience} />
              <ReviewItem label="Nível" value={form.level} />
              <ReviewItem label="Preço" value={form.product_price ? `R$ ${form.product_price}` : ""} />
              {form.has_discount && <ReviewItem label="Preço com desconto" value={form.discount_value ? `R$ ${form.discount_value}` : ""} />}
              <ReviewItem label="Comissão" value={form.commission_pct ? `${form.commission_pct}%` : ""} />
              <ReviewItem label="Página de vendas" value={form.sales_page_url} />
              <ReviewItem label="Plataforma" value={form.platform} />
              <ReviewItem label="Profissões" value={form.professions} />
              <ReviewItem label="Outras profissões" value={form.other_professions} />
              <ReviewItem label="Perpétuo" value={form.is_perpetual ? "Sim" : "Não"} />
            </div>
            <p className="text-xs text-center pt-3" style={{ color: "#99A6AB" }}>
              Clique em qualquer campo acima para editar. Quando tudo estiver correto, envie sua aplicação.
            </p>
          </div>
        );

      default:
        return null;
    }
  }

  // ─── Success View ─────────────────────────────────────────────────────────

  if (view === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#003326" }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="text-center max-w-lg"
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 150, damping: 15 }}
            className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8"
            style={{ background: "rgba(0,220,164,0.15)", border: "2px solid rgba(0,220,164,0.3)" }}
          >
            <CheckCircle2 className="w-12 h-12" style={{ color: "#00DCA4" }} />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold mb-4 tracking-tight"
            style={{ fontFamily: "Poppins, sans-serif", color: "#fff" }}
          >
            Recebemos sua aplicação!
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.6 }}
            className="text-base leading-relaxed mb-10"
            style={{ color: "#B0FCEA" }}
          >
            Nossa equipe vai analisar as informações e entraremos em contato em breve para seguir com os próximos passos.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            onClick={() => { setView("landing"); setCurrentStep(0); }}
            className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full text-sm font-bold transition-all duration-300 hover:shadow-lg hover:scale-[1.02] cursor-pointer"
            style={{ background: "#00DCA4", color: "#003326" }}
          >
            Voltar ao início <ArrowRight className="w-4 h-4" />
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // ─── Main Render ──────────────────────────────────────────────────────────

  return (
    <div className="h-screen w-screen overflow-hidden" style={{ fontFamily: "'Work Sans', sans-serif" }}>
      {/* Horizontal sliding container */}
      <motion.div
        className="flex h-full w-[200vw]"
        initial={false}
        animate={{ x: view === "landing" ? "0vw" : "-100vw" }}
        transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
      >
        {/* ─── Panel 1: Landing Page ───────────────────────────────────── */}
        <div className="w-screen h-full overflow-y-auto">
          {/* Hero */}
          <section
            className="relative"
            style={{ background: "#003326", minHeight: "100vh", overflow: "clip" }}
          >
            <div
              className="absolute inset-0"
              style={{
                background: "radial-gradient(ellipse 70% 50% at 50% 40%, rgba(0,220,164,0.12) 0%, transparent 70%)",
              }}
            />
            {/* Diagonal profile cards background */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ overflow: "clip" }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-30%",
                  left: "-20%",
                  width: "140%",
                  height: "160%",
                  transform: "rotate(-15deg)",
                  opacity: 0.09,
                  maskImage: "radial-gradient(ellipse 70% 60% at 50% 50%, black 0%, transparent 70%)",
                  WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 50%, black 0%, transparent 70%)",
                }}
              >
                {HERO_PROFILE_ROWS.map((row, rowIdx) => (
                  <div key={rowIdx} className="mb-4" style={{ overflow: "hidden" }}>
                    <motion.div
                      className="flex gap-4"
                      style={{ width: "max-content" }}
                      animate={{ x: row.direction === "left" ? [0, -row.offset] : [-row.offset, 0] }}
                      transition={{ duration: row.speed, repeat: Infinity, ease: "linear", repeatType: "loop" }}
                    >
                      {[...row.profiles, ...row.profiles, ...row.profiles].map((p, i) => (
                        <div
                          key={i}
                          className="flex-shrink-0 rounded-2xl p-3.5 flex items-center gap-3"
                          style={{
                            width: 220,
                            background: "rgba(255,255,255,0.12)",
                            border: "1px solid rgba(255,255,255,0.08)",
                          }}
                        >
                          <img
                            src={p.photo}
                            alt=""
                            className="w-9 h-9 rounded-full flex-shrink-0 object-cover"
                          />
                          <div className="min-w-0">
                            <p className="text-xs font-semibold truncate" style={{ color: "#fff" }}>{p.name}</p>
                            <p className="text-xs truncate" style={{ color: "rgba(176,252,234,0.6)" }}>{p.role}</p>
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 flex flex-col items-center justify-center text-center"
              style={{ minHeight: "100vh" }}
            >
              <motion.div initial="hidden" animate="visible" variants={stagger}>
                {/* Badge: Logo + Guia de Formações */}
                <motion.div variants={fadeUp} className="mb-10 flex justify-center">
                  <div
                    className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full relative overflow-hidden"
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      backdropFilter: "blur(12px)",
                      WebkitBackdropFilter: "blur(12px)",
                    }}
                  >
                    {/* Diagonal shimmer */}
                    <motion.div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.15) 45%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0.15) 55%, transparent 70%)",
                        backgroundSize: "200% 100%",
                      }}
                      animate={{ backgroundPosition: ["200% 0%", "-200% 0%"] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "linear", repeatDelay: 1 }}
                    />
                    <img
                      src="/logo-portus.png"
                      alt="Portüs"
                      className="h-5"
                      style={{ objectFit: "contain" }}
                    />
                    <div className="w-px h-4" style={{ background: "rgba(255,255,255,0.2)" }} />
                    <span
                      className="text-xs font-bold tracking-widest uppercase"
                      style={{ color: "#00DCA4", letterSpacing: "0.15em" }}
                    >
                      Guia de Formações
                    </span>
                  </div>
                </motion.div>
                <motion.h1
                  variants={fadeUp}
                  className="text-[2rem] sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 tracking-tight"
                  style={{ fontFamily: "Poppins, sans-serif", color: "#fff" }}
                >
                  Recomende seu curso para milhares de{" "}
                  <span style={{ color: "#00DCA4" }}>profissionais do digital.</span>
                </motion.h1>
                <motion.p
                  variants={fadeUp}
                  className="text-base sm:text-lg md:text-xl leading-relaxed mb-8 md:mb-10 max-w-2xl mx-auto"
                  style={{ color: "rgba(176,252,234,0.8)" }}
                >
                  O Guia de Formações da Portüs conecta experts e escolas ao
                  maior ecossistema de talentos digitais do Brasil.
                </motion.p>
                <motion.div variants={fadeUp}>
                  <button
                    onClick={enterForm}
                    className="group px-10 py-4 rounded-full text-base font-bold transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,220,164,0.3)] hover:scale-[1.03] flex items-center gap-3 mx-auto"
                    style={{ background: "#00DCA4", color: "#003326" }}
                  >
                    Aplicar como parceiro
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </motion.div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
                className="absolute bottom-10"
              >
                <ChevronDown className="w-6 h-6" style={{ color: "#00DCA4", opacity: 0.4 }} />
              </motion.div>
            </div>
          </section>

          {/* Benefits */}
          <section className="py-24 px-6 md:px-10" style={{ background: "#fff" }}>
            <div className="max-w-5xl mx-auto">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={stagger}
                className="text-center mb-16"
              >
                <motion.h2
                  variants={fadeUp}
                  className="text-3xl md:text-5xl font-bold mb-4 tracking-tight"
                  style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
                >
                  Por que ser parceiro?
                </motion.h2>
                <motion.p variants={fadeUp} className="text-base max-w-lg mx-auto" style={{ color: "#99A6AB" }}>
                  Realizamos uma curadoria de qualidade para garantir relevância. Seu curso será exposto na maior vitrine de profissionais do digital do Brasil: a plataforma de vagas da Portüs.
                </motion.p>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={stagger}
                className="grid grid-cols-1 md:grid-cols-3 gap-6"
              >
                {[
                  {
                    icon: TrendingUp,
                    title: "Exposição qualificada",
                    desc: "Seu curso recomendado para profissionais que já estão buscando capacitação na área digital.",
                  },
                  {
                    icon: Shield,
                    title: "Selo de verificação",
                    desc: "Cada curso recebe o selo 'Verificado pela Portüs', gerando mais confiança e conversão.",
                  },
                  {
                    icon: Award,
                    title: "Anuncie gratuitamente",
                    desc: "Você não paga nada para ter seu curso na plataforma. Somente um percentual de comissionamento.",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    className="rounded-3xl p-8 flex flex-col items-start transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                    style={{ background: "#FAFBFB", border: "1px solid #F2F4F4" }}
                  >
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                      style={{ background: "#EAFAF6" }}
                    >
                      <item.icon className="w-6 h-6" style={{ color: "#00664C" }} />
                    </div>
                    <h3 className="font-bold text-base mb-2" style={{ color: "#003326" }}>
                      {item.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: "#646C6F" }}>
                      {item.desc}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* Showcase */}
          <section className="py-24 px-6 md:px-10" style={{ background: "#F7F8F8", overflow: "hidden" }}>
            <div className="max-w-5xl mx-auto">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={stagger}
                className="text-center mb-16"
              >
                <motion.h2
                  variants={fadeUp}
                  className="text-3xl md:text-5xl font-bold mb-4 tracking-tight"
                  style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
                >
                  Como seu curso aparece em nossa plataforma
                </motion.h2>
                <motion.p variants={fadeUp} className="text-base max-w-lg mx-auto" style={{ color: "#99A6AB" }}>
                  Após aprovação, seu curso ganha um card premium com selo de verificação
                  e é recomendado para profissionais compatíveis.
                </motion.p>
              </motion.div>

              {/* Mac Browser Frame */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="rounded-2xl overflow-hidden"
                style={{ background: "#fff", boxShadow: "0 25px 80px rgba(0,51,38,0.12), 0 8px 32px rgba(0,0,0,0.08)", border: "1px solid #E8EAEB" }}
              >
                {/* Title bar */}
                <div className="flex items-center px-4 py-3" style={{ background: "#F7F8F8", borderBottom: "1px solid #E8EAEB" }}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: "#FF5F57" }} />
                    <div className="w-3 h-3 rounded-full" style={{ background: "#FEBC2E" }} />
                    <div className="w-3 h-3 rounded-full" style={{ background: "#28C840" }} />
                  </div>
                </div>

                {/* Browser content */}
                <div className="pt-5 pb-4" style={{ background: "#FAFBFB" }}>
                  {/* Badge */}
                  <div className="flex justify-center mb-4">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: "#EAFAF6", border: "1px solid rgba(0,220,164,0.2)" }}>
                      <Sparkles className="w-3 h-3" style={{ color: "#00DCA4" }} />
                      <span className="text-xs font-semibold" style={{ color: "#003326" }}>Cursos recomendados para você</span>
                    </div>
                  </div>

                  {/* Carousel inside browser */}
                  <ShowcaseCarousel courses={SHOWCASE_COURSES} />
                </div>
              </motion.div>
            </div>
          </section>

          {/* How it works */}
          <section className="py-24 px-6 md:px-10" style={{ background: "#fff" }}>
            <div className="max-w-3xl mx-auto">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={stagger}
                className="text-center mb-16"
              >
                <motion.h2
                  variants={fadeUp}
                  className="text-3xl md:text-5xl font-bold mb-4 tracking-tight"
                  style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
                >
                  Simples assim.
                </motion.h2>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={stagger}
                className="space-y-0"
              >
                {[
                  { n: "01", title: "Preencha o formulário", desc: "Conte sobre seu curso, público-alvo e condições comerciais." },
                  { n: "02", title: "Nossa equipe analisa", desc: "Revisamos sua proposta para garantir alinhamento com a plataforma." },
                  { n: "03", title: "Seu curso na plataforma", desc: "Após aprovação, seu curso aparece no Guia de Formações com selo verificado." },
                ].map((step, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    className="flex items-start gap-6 py-8"
                    style={{ borderBottom: i < 2 ? "1px solid #F2F4F4" : "none" }}
                  >
                    <span
                      className="text-4xl md:text-5xl font-bold flex-shrink-0"
                      style={{ fontFamily: "Poppins, sans-serif", color: "#00DCA4", opacity: 0.4, minWidth: "60px" }}
                    >
                      {step.n}
                    </span>
                    <div>
                      <h3 className="font-bold text-lg mb-1" style={{ color: "#003326" }}>
                        {step.title}
                      </h3>
                      <p className="text-sm leading-relaxed" style={{ color: "#646C6F" }}>
                        {step.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-20 px-6 md:px-10" style={{ background: "#003326" }}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={stagger}
              className="max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 text-center"
            >
              {[
                { number: "+25 mil", label: "Profissionais ativos" },
                { number: "+3.000", label: "Contratados" },
              ].map((stat, i) => (
                <motion.div key={i} variants={fadeUp}>
                  <p
                    className="text-4xl md:text-5xl font-bold mb-2 tracking-tight"
                    style={{ fontFamily: "Poppins, sans-serif", color: "#00DCA4" }}
                  >
                    {stat.number}
                  </p>
                  <p className="text-sm" style={{ color: "rgba(176,252,234,0.7)" }}>
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </section>

          {/* CTA */}
          <section className="py-28 px-6 md:px-10" style={{ background: "#fff" }}>
            <div className="max-w-2xl mx-auto text-center">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
                <motion.h2
                  variants={fadeUp}
                  className="text-3xl md:text-5xl font-bold mb-5 tracking-tight"
                  style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
                >
                  Pronto para começar?
                </motion.h2>
                <motion.p variants={fadeUp} className="text-base mb-10" style={{ color: "#99A6AB" }}>
                  Junte-se ao maior ecossistema de talentos digitais do Brasil.
                </motion.p>
                <motion.div variants={fadeUp}>
                  <button
                    onClick={enterForm}
                    className="group px-10 py-4 rounded-full text-base font-bold transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,220,164,0.3)] hover:scale-[1.03] inline-flex items-center gap-3"
                    style={{ background: "#003326", color: "#00DCA4" }}
                  >
                    Aplicar agora
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* Footer */}
          <footer className="py-8 px-6 text-center" style={{ background: "#003326", borderTop: "1px solid rgba(0,220,164,0.1)" }}>
            <span className="text-sm" style={{ color: "#99A6AB" }}>
              &copy; 2026 Portüs. Todos os direitos reservados.
            </span>
          </footer>
        </div>

        {/* ─── Panel 2: Multi-Step Form ────────────────────────────────── */}
        <div className="w-screen h-full overflow-y-auto" style={{ background: "#fff" }}>
          <div className="min-h-full flex flex-col pt-2 md:pt-6">
            {/* Form header area */}
            <div className="flex flex-col">
              {/* Top bar with back button + progress */}
              <div className="px-6 md:px-10 py-4 md:py-6 flex items-center gap-4">
                <button
                  onClick={currentStep === 0 ? () => setView("landing") : prevStep}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 flex-shrink-0"
                  style={{ background: "#F2F4F4" }}
                >
                  <ArrowLeft className="w-4 h-4" style={{ color: "#2E3233" }} />
                </button>
                <div className="flex-1">
                  <StepProgressBar currentStep={currentStep} totalSteps={STEPS.length} />
                </div>
              </div>

              {/* Step dots */}
              <div className="flex justify-center gap-2 px-6 mb-1 md:mb-2">
                {STEPS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      if (i < currentStep) goToStep(i);
                    }}
                    className="transition-all duration-300"
                    style={{
                      width: i === currentStep ? 24 : 8,
                      height: 8,
                      borderRadius: 4,
                      background: i <= currentStep ? "#003326" : "#E5E9EA",
                      cursor: i < currentStep ? "pointer" : "default",
                    }}
                  />
                ))}
              </div>

              {/* Step content */}
              <div className="flex flex-col px-6 md:px-10 py-4 md:py-8 max-w-lg mx-auto w-full">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={currentStep}
                    custom={direction}
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                  >
                    {/* Step icon + title + subtitle */}
                    <div className="mb-6 md:mb-10">
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className="w-11 h-11 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-5"
                        style={{ background: "#EAFAF6" }}
                      >
                        {React.createElement(STEPS[currentStep].icon, {
                          className: "w-6 h-6",
                          style: { color: "#00664C" },
                        })}
                      </motion.div>
                      <h2
                        className="text-xl md:text-3xl font-bold mb-2 md:mb-3 tracking-tight"
                        style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
                      >
                        {STEPS[currentStep].title}
                      </h2>
                      <p className="text-sm leading-relaxed" style={{ color: "#99A6AB" }}>
                        {STEPS[currentStep].subtitle}
                      </p>
                    </div>

                    {/* Fields */}
                    {renderStepContent(currentStep)}
                  </motion.div>
                </AnimatePresence>

                {/* Navigation buttons */}
                <div className="mt-6 md:mt-10 pb-6 md:pb-10">
                  {currentStep < STEPS.length - 1 ? (
                    <button
                      onClick={nextStep}
                      className="group w-full py-4 rounded-2xl text-base font-bold flex items-center justify-center gap-2.5 transition-all duration-300 hover:shadow-lg hover:scale-[1.01]"
                      style={{ background: "#003326", color: "#fff" }}
                    >
                      Continuar
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="group w-full py-4 rounded-2xl text-base font-bold flex items-center justify-center gap-2.5 transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,220,164,0.3)] hover:scale-[1.01] disabled:opacity-60"
                      style={{ background: "#00DCA4", color: "#003326" }}
                    >
                      {submitting ? (
                        <>
                          <div
                            className="w-5 h-5 border-2 rounded-full animate-spin"
                            style={{ borderColor: "#003326", borderTopColor: "transparent" }}
                          />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Enviar Aplicação
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
